/* eslint-disable @typescript-eslint/no-shadow */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { routes, routes2 } from '@linqpal/models'
import { PayWithBluetapeCreditFlow_IDLE } from '../components/PayWithBluetapeCreditFlow_IDLE'
import { PayWithBluetapeCreditFlow_PLAN_SELECTED } from '../components/PayWithBluetapeCreditFlow_PLAN_SELECTED'
import { PayWithBluetapeCreditFlow_SUCCESS } from '../components/PayWithBluetapeCreditFlow_SUCCESS'
import { fileDownloader } from '@linqpal/common-frontend/src/helpers'
import type { Form as AddNewProjectFlowPayload } from '../components/AddNewProjectModal'
import { AddNewProjectModal } from '../components/AddNewProjectModal'
import { SelectProjectModal } from '../components/SelectProjectModal'
import { Project_NewFormat } from '../projectMapper/Project_NewFormat'
import { mapNewProjectStructureToOld } from '../projectMapper/mapNewProjectStructureToOld'
import {
  IntegrationErrorCodesType,
  PURCHASE_TYPE,
  unexpectedErrorCode,
} from '@linqpal/models/src/dictionaries'
import { PayWithCreditCardFlow_ERROR } from '../components/PayWithCreditCardFlow_ERROR'
import { useTranslation } from 'react-i18next'
import { titleText } from '../utils/paymentModalTexts'
import { handleError } from '../helpers/getErrorMetadata'

enum STEPS {
  NONE,
  IDLE,
  PLAN_SELECTED,
  SUCCESS,
  ADD_NEW_PROJECT,
  SELECT_PROJECT,
  ERROR,
}

interface State {
  selectedPaymentPlan?: any
}

interface PayWithBluetapeCreditFlowProps {
  invoiceId: string | null
  totalAmount: number
  companyName: string
  paymentMethodName: string
  userProjects: any[]
  paymentPlans: any[]
  invoice_connector_project?: any
  invoice_connector_integration_id?: any
  company_credit_purchaseType?: any
  jwtToken?: string
  invoice?: any

  onSuccess: () => void
  onClose: () => void
  onFail: () => void
}

export const PayWithBluetapeCreditFlow: React.FC<PayWithBluetapeCreditFlowProps> =
  ({
    invoiceId,
    invoice,
    jwtToken,
    totalAmount,
    companyName,
    paymentMethodName,
    userProjects,
    paymentPlans,
    invoice_connector_project,
    invoice_connector_integration_id,
    company_credit_purchaseType,

    onSuccess,
    onClose,
    onFail,
  }) => {
    const [step, setStep] = useState(STEPS.NONE)
    const [loading, setLoading] = useState<boolean>(false)
    const [state, setState] = useState<State>({})
    const [project, setProject] = useState<any | null>(null)
    const [status, setStatus] = useState('')
    const [message, setMessage] = useState('')
    const [errorCode, setErrorCode] =
      useState<null | IntegrationErrorCodesType>(null)
    const { t } = useTranslation(['global', 'errors'])

    const errorCodeRef = useRef(errorCode)

    useEffect(() => {
      errorCodeRef.current = errorCode
    }, [errorCode])

    const isProjectForLinkingMandatory =
      company_credit_purchaseType === PURCHASE_TYPE.INVENTORY ||
      company_credit_purchaseType === PURCHASE_TYPE.PROJECT

    const bootstrap = useCallback(async () => {
      if (!userProjects) throw new Error('user projects still empty')

      const integration_id = invoice_connector_integration_id
      const external_project_id = invoice_connector_project?.external_project_id

      if (invoice_connector_project) {
        if (userProjects.length > 0) {
          const findProject = userProjects.find((project) => {
            return (
              project?.connector?.integration_id === integration_id &&
              project?.connector?.external_project_id === external_project_id
            )
          })
          if (findProject) {
            setProject(findProject)
            setStep(STEPS.IDLE)
            return
          } else {
            setProject(invoice_connector_project)
            setStep(STEPS.ADD_NEW_PROJECT)
            return
          }
        } else {
          setProject(invoice_connector_project)
          setStep(STEPS.ADD_NEW_PROJECT)
          return
        }
      } else {
        if (isProjectForLinkingMandatory) {
          if (userProjects.length > 0) {
            setStep(STEPS.SELECT_PROJECT)
            return
          } else {
            setStep(STEPS.ADD_NEW_PROJECT)
            return
          }
        } else {
          setStep(STEPS.IDLE)
          return
        }
      }
    }, [
      invoice_connector_integration_id,
      invoice_connector_project,
      userProjects,
      isProjectForLinkingMandatory,
    ])

    useEffect(() => {
      bootstrap()
    }, [bootstrap])

    const handleNext = useCallback(
      async (payload?: any) => {
        if (step === STEPS.SELECT_PROJECT) {
          setProject(project)
          setStep(STEPS.IDLE)
          return
        }

        if (step === STEPS.ADD_NEW_PROJECT) {
          // eslint-disable-next-line prettier/prettier
          const {
            jobId,
            streetAddress,
            unitNumber,
            city,
            state,
            zip,
            projectType,
            firstName,
            lastName,
          } = payload as AddNewProjectFlowPayload

          let type: 'Default' | 'Public' | 'Private' = 'Default'
          if (projectType === 'commercial') type = 'Public'
          if (projectType === 'residential') type = 'Private'

          const addressProperties = [
            streetAddress,
            unitNumber,
            state,
            city,
            zip,
          ].filter(Boolean)

          const address = addressProperties.join(', ')

          const projectInfo: Project_NewFormat = {
            address: streetAddress,
            jobId,
            type,
            // eslint-disable-next-line prettier/prettier
            jobAddress: {
              address: streetAddress,
              unitNo: unitNumber,
              state,
              zipCode: zip,
              city,
            },
            // eslint-disable-next-line prettier/prettier
            primeContractorDetails: {
              firstName,
              lastName,
            },
          }
          // eslint-disable-next-line prettier/prettier
          const { data: oldProjectStructureData } =
            mapNewProjectStructureToOld(projectInfo)

          const { data } = await routes2.project.create({
            data: {
              currentSection: '',
              data: oldProjectStructureData,
            },
            address,
            connector: {
              // eslint-disable-next-line prettier/prettier
              integration_id: invoice_connector_integration_id as
                | string
                | undefined,
              // eslint-disable-next-line prettier/prettier
              external_project_id:
                invoice_connector_project?.external_project_id,
              source_modified_date: new Date().toISOString(),
            },
          })

          setProject(data)
          setStep(STEPS.IDLE)
          return
        }

        if (step === STEPS.IDLE) {
          setState({ ...state, selectedPaymentPlan: payload })
          setStep(STEPS.PLAN_SELECTED)
          return
        }

        if (step === STEPS.PLAN_SELECTED) {
          setLoading(true)
          try {
            if (!state.selectedPaymentPlan) {
              throw new Error('No payment plan selected')
            }

            const projectId = project ? project._id || project.id : null

            let invoiceIdForPay = invoiceId

            if (!invoiceIdForPay) {
              if (!jwtToken) {
                setErrorCode(unexpectedErrorCode)
                throw new Error('JWT token is missing')
              }
              const { invoiceId: newInvoiceId, errorCode: error } =
                await routes.integration.upsertInvoiceFromJwt(jwtToken)

              if (error) {
                setErrorCode(error)
                throw new Error('Error occurred while upserting invoice')
              }
              invoiceIdForPay = newInvoiceId
            }

            if (!invoiceIdForPay) {
              setErrorCode(unexpectedErrorCode)
              throw new Error('Invoice ID is missing')
            }
            await routes2.user.handleLoanApplication(
              {
                invoiceId: [invoiceIdForPay],
                paymentPlan: state.selectedPaymentPlan.id,
                cardId: undefined,
              },
              projectId,
            )

            setStep(STEPS.SUCCESS)
          } catch (err: any) {
            const { status, message } = handleError(
              errorCodeRef.current,
              err,
              t,
            )

            setStatus(status)
            setMessage(message)
            setStep(STEPS.ERROR)
          } finally {
            setLoading(false)
          }
        }
      },
      [
        step,
        project,
        invoice_connector_integration_id,
        invoice_connector_project?.external_project_id,
        state,
        invoiceId,
        jwtToken,
        t,
      ],
    )

    const handleBack = useCallback(() => {
      if (step === STEPS.PLAN_SELECTED) {
        setStep(STEPS.IDLE)
      }
    }, [step])

    const handleClose = useCallback(() => {
      onClose()
    }, [onClose])

    const handleDownloadAgreement = useCallback(async () => {
      try {
        let url: string | undefined, filename: string | undefined

        if (!invoiceId) {
          if (invoice) {
            ;({ url, filename } =
              await routes.contractor.makeIntegrationAgreement({
                paymentPlan: state.selectedPaymentPlan,
                supplierCompanyId: invoice.company?.id,
                totalAmount: invoice?.total_amount,
              }))
          }
        } else {
          ;({ url, filename } = await routes.contractor.makeAgreement({
            invoiceIds: [invoiceId],
            paymentPlan: state.selectedPaymentPlan,
          }))
        }

        fileDownloader(url, {}, filename)
      } catch (err) {
        console.error(err)
      }
    }, [invoiceId, state.selectedPaymentPlan, invoice])

    const handleSetStepAddNewProject = useCallback(() => {
      setStep(STEPS.ADD_NEW_PROJECT)
    }, [])

    const firstindividualOwner = project?.individualOwners
      ? project?.individualOwners[0]
      : undefined

    const addNewProjectInitialValues: Partial<AddNewProjectFlowPayload> = {
      jobId: project?.jobId,
      streetAddress: project?.jobAddress?.address,
      unitNumber: project?.jobAddress?.unitNo,
      city: project?.jobAddress?.city,
      state: project?.jobAddress?.state,
      zip: project?.jobAddress?.zipCode,
      projectType:
        project?.type === 'Default' || 'Public' ? 'commercial' : 'residential',
      firstName: firstindividualOwner?.firstName,
      lastName: firstindividualOwner?.lastName,
    }

    return (
      <>
        {step === STEPS.NONE && null}
        {step === STEPS.IDLE && (
          <PayWithBluetapeCreditFlow_IDLE
            totalAmount={totalAmount}
            paymentPlans={paymentPlans}
            onSelectPaymentPlan={handleNext}
            onClose={handleClose}
          />
        )}
        {step === STEPS.PLAN_SELECTED && state.selectedPaymentPlan && (
          <PayWithBluetapeCreditFlow_PLAN_SELECTED
            onBack={handleBack}
            onClose={handleClose}
            onPay={handleNext}
            onAgreementPress={handleDownloadAgreement}
            loading={loading}
            disabled={false}
            totalAmount={totalAmount}
            fee={state.selectedPaymentPlan.fee}
            companyName={companyName}
            paymentMethodName={paymentMethodName}
            agreementDownloading={false}
            days={state.selectedPaymentPlan.days}
            frequency={state.selectedPaymentPlan.frequency}
            term={state.selectedPaymentPlan.term}
          />
        )}
        {step === STEPS.SUCCESS && (
          <PayWithBluetapeCreditFlow_SUCCESS
            onSuccess={() => {
              handleClose()
              onSuccess?.()
            }}
          />
        )}
        {step === STEPS.ADD_NEW_PROJECT && (
          <AddNewProjectModal
            initialValues={addNewProjectInitialValues}
            onCreate={handleNext}
            onClose={handleClose}
          />
        )}
        {step === STEPS.SELECT_PROJECT && !!userProjects && (
          <SelectProjectModal
            onClose={handleClose}
            onAddNewProject={handleSetStepAddNewProject}
            onProjectSelected={handleNext}
            isProjectForLinkingMandatory={isProjectForLinkingMandatory}
          />
        )}
        {step === STEPS.ERROR && (
          <PayWithCreditCardFlow_ERROR
            title={errorCode ? status : titleText(status)}
            subtitle={message}
            paymentMethodSubtitle={''}
            paymentMethodName={paymentMethodName}
            onContinue={handleClose}
            onClose={() => {
              handleClose()
              if (onFail) onFail()
            }}
          />
        )}
      </>
    )
  }
